/**
 * Action Register + Verification Log Graph I/O (13-07): the ONE layer the outbox (13-06), the
 * queue (13-08/09), the Verify page (13-11) and the release sitting (13-12) use.
 *
 *   - Reads keep the item envelope: `@odata.etag` (sent back as `If-Match`), `createdBy` (the
 *     delegated user SharePoint stamped as Author). Graph OMITS an empty field entirely, so every
 *     mapper treats a missing key as null.
 *   - Every list is addressed by GUID (`AR_LISTS`), never by title.
 *   - Writes: PATCH `…/fields` with If-Match FIRST, then the audit row(s). A 412 is re-read: if the
 *     row already holds our values and we were its last editor, the change was ours (a crash
 *     between the PATCH and `progress.patched()`), so it is adopted and the audit rows posted.
 *   - Audit retries de-dupe against the audit list by `[a:<actionId>]` + `FieldChanged`.
 *   - 13.2-07: the one AR create the dashboard makes, an incident follow-up (`createFollowUp`): POST the item,
 *     then its "created" audit row (never `ActorUPN`, ISS-285); a failed audit row is owed (`postFollowUpAudit`).
 *   - Author is the list item's `createdBy`, never the `Actor` field (the auditService
 *     `readActor` bug class: `Actor` is a Person lookup that reads back as `ActorLookupId`).
 *
 * Live read-only proof (orchestrator, 3 Oct 2026, as andy@): the AR items read, the User
 * Information List lookup and the audit `ParentRef` filter all return 200 in the shapes built here.
 * No React. Retry policy: `graphClient.withRetry` (the SDK's own retry is off on these requests).
 */

import type { Client } from '@microsoft/microsoft-graph-client';
import {
  AR_FIELDS,
  AR_LISTS,
  AR_LOOKUP_FIELDS,
  RELEASE_BATCH_SIZE,
  VL_FIELDS,
  VL_LOOKUP_FIELDS,
  VL_MUTABLE_FIELDS,
  actionRef,
  verificationRef,
} from '../config/actionRegister';
import { AUDIT_FIELDS, REGISTERS } from '../config/registers';
import {
  backoffMs,
  createItemFields,
  eTagOf,
  errorStatus,
  getItemEnvelope,
  getListId,
  listItemEnvelopes,
  odataLiteral,
  postBatch,
  realSleep,
  retryAfterOf,
  updateItemFields,
} from './graphClient';
import type { GraphBatchRequest, GraphListItemEnvelope, RetryOptions } from './graphClient';
import { buildAuditRequests, buildPatchRequests, chunk, MAX_BATCH, partition } from '../lib/batch';
import type { BatchResult, ListTarget } from '../lib/batch';
import { buildVerb, countRejections, VerbError } from '../lib/actionVerbs';
import { canDoVerb } from '../lib/actionAuthority';
import { vlCorrect, vlReject, vlVerify } from '../lib/vlVerbs';
import type { VlCorrectContext, VlCorrectVerdict } from '../lib/vlVerbs';
import { actionTag, noteHasAction } from '../lib/outbox';
import { stampCreatedAudit } from '../lib/followUps';
import type { CreatedAuditDraft } from '../lib/followUps';
import type { CommitFn, CommitProgress, CommitResult, OutboxEntry } from '../lib/outbox';
import type { AuditEntryType } from '../types/qms';
import type {
  ActionRow,
  Actor,
  AuditRowDraft,
  VerificationRow,
  VerbResult,
} from '../types/actionRegister';

// ---------------------------------------------------------------------------
// I/O context
// ---------------------------------------------------------------------------

/** A synchronous key/value store (sessionStorage-shaped). Its methods may throw. */
export interface SessionStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface ActionIO {
  client: Client;
  /** The resolved Q&S site id (from useGraphClient). */
  siteId: string;
  /** Passed to every Graph call (tests inject `sleep`). */
  retry?: RetryOptions;
  /** Where `resolveActor` caches. Default `globalThis.sessionStorage` (null = no cache). */
  sessionStore?: SessionStore | null;
  /** Epoch ms. Default `Date.now`. */
  now?: () => number;
  /** Default `crypto.randomUUID`. */
  randomId?: () => string;
}

export type ListKind = 'AR' | 'VL';

const PARENT_LIST: Record<ListKind, string> = {
  AR: AR_LISTS.actionRegister,
  VL: AR_LISTS.verificationLog,
};

const AUDIT_LIST: Record<ListKind, string> = {
  AR: AR_LISTS.actionRegisterAudit,
  VL: AR_LISTS.verificationLogAudit,
};

/**
 * The site's hidden User Information List (D-13-00-4). The GUID was read live on 3 Oct 2026; if
 * it ever 404s (a different site), the list is resolved by its display name instead.
 */
export const USER_INFO_LIST = {
  id: '4fe8e70b-54b9-46ec-af76-35ddbe7b5c8e',
  displayName: 'User Information List',
} as const;

// ---------------------------------------------------------------------------
// $select lists (inside $expand=fields(...), never $select=fields/X)
// ---------------------------------------------------------------------------

const PERSON_COLUMNS = new Set<string>([AR_FIELDS.owner, AR_FIELDS.verifiedBy]);

/** The Action Register read: every column, Person columns as `…LookupId`, plus Created / Author. */
export const AR_SELECT: readonly string[] = [
  ...Object.values(AR_FIELDS).filter((f) => !PERSON_COLUMNS.has(f)),
  AR_LOOKUP_FIELDS.owner,
  AR_LOOKUP_FIELDS.verifiedBy,
  'Created',
  'AuthorLookupId',
];

/** The Verification Log read. */
export const VL_SELECT: readonly string[] = [
  ...Object.values(VL_FIELDS).filter((f) => f !== VL_FIELDS.verifiedBy),
  VL_LOOKUP_FIELDS.verifiedBy,
  'Created',
  'AuthorLookupId',
];

/** The audit-sibling read (`Actor` as `ActorLookupId`; Author comes from the item's createdBy). */
export const AUDIT_SELECT: readonly string[] = [
  AUDIT_FIELDS.title,
  AUDIT_FIELDS.parentRef,
  AUDIT_FIELDS.entryType,
  AUDIT_FIELDS.fieldChanged,
  AUDIT_FIELDS.oldValue,
  AUDIT_FIELDS.newValue,
  AUDIT_FIELDS.note,
  AUDIT_FIELDS.entryTimestamp,
  `${AUDIT_FIELDS.actor}LookupId`,
];

// ---------------------------------------------------------------------------
// Mapping (a missing key is null: Graph omits empty fields)
// ---------------------------------------------------------------------------

type Fields = Record<string, unknown>;

function str(f: Fields, key: string): string | null {
  const v = f[key];
  if (v === undefined || v === null || v === '') return null;
  return typeof v === 'string' ? v : String(v);
}

function lookup(f: Fields, key: string): string | null {
  const v = f[key];
  if (v === undefined || v === null || v === '') return null;
  return String(v);
}

function bool(f: Fields, key: string): boolean {
  const v = f[key];
  return v === true || v === 1 || v === '1' || (typeof v === 'string' && /^(true|yes)$/i.test(v));
}

function num(f: Fields, key: string): number | null {
  const v = f[key];
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** A choice column: missing → the column's provisioned default; present → passed through. */
function choice<T extends string>(f: Fields, key: string, fallback: T): T {
  return (str(f, key) ?? fallback) as T;
}

function authorEmailOf(item: GraphListItemEnvelope): string | null {
  const e = item.createdBy?.user?.email;
  return typeof e === 'string' && e.trim() !== '' ? e.trim() : null;
}

/** Graph Action Register item → `ActionRow`. Defaults are the 09.1 provisioned column defaults. */
export function mapActionItem(item: GraphListItemEnvelope): ActionRow {
  const f: Fields = item.fields ?? {};
  const id = Number(item.id);
  const F = AR_FIELDS;
  return {
    id,
    reference: actionRef(id),
    eTag: eTagOf(item) ?? str(f, '@odata.etag') ?? '',
    created: str(f, 'Created') ?? item.createdDateTime ?? '',
    authorEmail: authorEmailOf(item),
    title: str(f, F.title) ?? '',
    kind: choice(f, F.kind, 'Action'),
    source: choice(f, F.source, 'Other'),
    sourceRef: str(f, F.sourceRef),
    parentType: str(f, F.parentType) as ActionRow['parentType'],
    parentId: str(f, F.parentId),
    actionType: str(f, F.actionType),
    description: str(f, F.description),
    options: str(f, F.options),
    decidedOption: str(f, F.decidedOption),
    ownerRole: choice(f, F.ownerRole, 'Oversight'),
    ownerLookupId: lookup(f, AR_LOOKUP_FIELDS.owner),
    owner: str(f, F.owner),
    status: choice(f, F.status, 'Proposed'),
    tier: choice(f, F.tier, 'write-then-verify'),
    dueDate: str(f, F.dueDate),
    verificationMethod: choice(f, F.verificationMethod, 'self_attestation'),
    evidence: str(f, F.evidence),
    acceptedAt: str(f, F.acceptedAt),
    completedAt: str(f, F.completedAt),
    verifiedByLookupId: lookup(f, AR_LOOKUP_FIELDS.verifiedBy),
    verifiedBy: str(f, F.verifiedBy),
    verifiedAt: str(f, F.verifiedAt),
    verdict: choice(f, F.verdict, 'pending'),
    escalation: choice(f, F.escalation, 'none'),
    createdByAgent: bool(f, F.createdByAgent),
    cardId: str(f, F.cardId),
    executor: choice(f, F.executor, 'Human'),
    releasedAt: str(f, F.releasedAt),
    releaseNote: str(f, F.releaseNote),
    releasedBy: str(f, F.releasedBy),
    decisionClass: str(f, F.decisionClass) as ActionRow['decisionClass'],
  };
}

/** Graph Verification Log item → `VerificationRow`. */
export function mapVerificationItem(item: GraphListItemEnvelope): VerificationRow {
  const f: Fields = item.fields ?? {};
  const id = Number(item.id);
  const V = VL_FIELDS;
  return {
    id,
    reference: verificationRef(id),
    eTag: eTagOf(item) ?? str(f, '@odata.etag') ?? '',
    created: str(f, 'Created') ?? item.createdDateTime ?? '',
    authorEmail: authorEmailOf(item),
    title: str(f, V.title) ?? '',
    cardId: str(f, V.cardId) ?? '',
    idempotencyKey: str(f, V.idempotencyKey),
    step: num(f, V.step),
    targetSystem: choice(f, V.targetSystem, 'Other'),
    recordRef: str(f, V.recordRef) ?? '',
    action: str(f, V.action) ?? '',
    payloadHash: str(f, V.payloadHash),
    agentIdentity: str(f, V.agentIdentity) ?? '',
    writtenAt: str(f, V.writtenAt) ?? '',
    verifierRole: str(f, V.verifierRole) as VerificationRow['verifierRole'],
    verifiedByLookupId: lookup(f, VL_LOOKUP_FIELDS.verifiedBy),
    verifiedBy: str(f, V.verifiedBy),
    verifiedAt: str(f, V.verifiedAt),
    verdict: choice(f, V.verdict, 'pending'),
    exceptionReason: str(f, V.exceptionReason),
  };
}

/** One audit-sibling row as read. `authorEmail` is the item's createdBy (who really wrote it). */
export interface AuditRecord {
  id: number;
  title: string | null;
  parentRef: string;
  entryTimestamp: string | null;
  entryType: AuditEntryType | null;
  fieldChanged: string | null;
  oldValue: string | null;
  newValue: string | null;
  note: string | null;
  actorLookupId: string | null;
  authorEmail: string | null;
  created: string | null;
}

export function mapAuditItem(item: GraphListItemEnvelope): AuditRecord {
  const f: Fields = item.fields ?? {};
  const A = AUDIT_FIELDS;
  return {
    id: Number(item.id),
    title: str(f, A.title),
    parentRef: str(f, A.parentRef) ?? '',
    entryTimestamp: str(f, A.entryTimestamp),
    entryType: str(f, A.entryType) as AuditEntryType | null,
    fieldChanged: str(f, A.fieldChanged),
    oldValue: str(f, A.oldValue),
    newValue: str(f, A.newValue),
    note: str(f, A.note),
    actorLookupId: lookup(f, `${A.actor}LookupId`),
    authorEmail: authorEmailOf(item),
    created: item.createdDateTime ?? null,
  };
}

function timeOf(r: AuditRecord): number {
  const t = Date.parse(r.entryTimestamp ?? r.created ?? '');
  return Number.isNaN(t) ? 0 : t;
}

/** Chronological: EntryTimestamp, then item id. */
export function sortAudit(rows: readonly AuditRecord[]): AuditRecord[] {
  return [...rows].sort((a, b) => timeOf(a) - timeOf(b) || a.id - b.id);
}

/** An audit draft → the SP fields of a new audit item. Empty values are omitted. */
export function auditFields(draft: AuditRowDraft): Record<string, unknown> {
  const A = AUDIT_FIELDS;
  const out: Record<string, unknown> = {
    [A.title]: draft.title,
    [A.parentRef]: draft.parentRef,
    [A.entryTimestamp]: draft.entryTimestamp,
    [A.entryType]: draft.entryType,
  };
  if (draft.fieldChanged !== null) out[A.fieldChanged] = draft.fieldChanged;
  if (draft.oldValue !== null) out[A.oldValue] = draft.oldValue;
  if (draft.newValue !== null) out[A.newValue] = draft.newValue;
  if (draft.note !== null) out[A.note] = draft.note;
  if (draft.actorLookupId) out[`${A.actor}LookupId`] = draft.actorLookupId;
  return out;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** Every Action Register row, with its eTag, paged through `@odata.nextLink`. */
export async function listActionRows(io: ActionIO): Promise<ActionRow[]> {
  const items = await listItemEnvelopes(io.client, io.siteId, AR_LISTS.actionRegister, {
    select: AR_SELECT,
    retry: io.retry,
  });
  return items.map(mapActionItem);
}

/** One Action Register row (e.g. the refetch after a conflict). */
export async function getActionRow(io: ActionIO, id: number): Promise<ActionRow> {
  const item = await getItemEnvelope(io.client, io.siteId, AR_LISTS.actionRegister, id, AR_SELECT, io.retry);
  return mapActionItem(item);
}

/**
 * Every Verification Log row. `pendingOnly` filters client-side (the log is small, and a
 * server-side `Verdict` filter is a query shape not proven live).
 */
export async function listVerificationRows(
  io: ActionIO,
  opts: { pendingOnly?: boolean } = {},
): Promise<VerificationRow[]> {
  const items = await listItemEnvelopes(io.client, io.siteId, AR_LISTS.verificationLog, {
    select: VL_SELECT,
    retry: io.retry,
  });
  const rows = items.map(mapVerificationItem);
  return opts.pendingOnly ? rows.filter((r) => r.verdict === 'pending') : rows;
}

/** Lists whose `ParentRef` filter returned 400: read-all-and-index from then on. */
const filterUnsupported = new Set<string>();
/** The fallback index, per list, built at most once per session unless `fresh`. */
const auditIndexCache = new Map<string, Promise<Map<string, AuditRecord[]>>>();
/** `resolveActor` results this session ('' = not found). */
const actorMemo = new Map<string, string>();

/** Test hook: forget every per-session cache. */
export function resetActionServiceCaches(): void {
  filterUnsupported.clear();
  auditIndexCache.clear();
  actorMemo.clear();
}

function readAuditIndex(io: ActionIO, listId: string, fresh: boolean): Promise<Map<string, AuditRecord[]>> {
  const key = `${io.siteId}:${listId}`;
  const cached = auditIndexCache.get(key);
  if (cached && !fresh) return cached;
  const p = listItemEnvelopes(io.client, io.siteId, listId, { select: AUDIT_SELECT, retry: io.retry }).then(
    (items) => {
      const index = new Map<string, AuditRecord[]>();
      for (const r of items.map(mapAuditItem)) {
        const list = index.get(r.parentRef) ?? [];
        list.push(r);
        index.set(r.parentRef, list);
      }
      return index;
    },
  );
  auditIndexCache.set(key, p);
  p.catch(() => {
    if (auditIndexCache.get(key) === p) auditIndexCache.delete(key);
  });
  return p;
}

/**
 * The audit rows of one parent (`ACT-<id>` or `VER-<id>`), oldest first. Default: the
 * `fields/ParentRef eq '<ref>'` filter with the Prefer header (proven live, 200). Only if that
 * returns 400 does the list fall back to read-all-and-index, cached per session; `fresh: true`
 * re-reads (de-dupe and replay checks must never trust a stale index).
 */
export async function listAuditFor(
  io: ActionIO,
  parentRef: string,
  opts: { target?: ListKind; fresh?: boolean } = {},
): Promise<AuditRecord[]> {
  if (!parentRef) return [];
  const listId = AUDIT_LIST[opts.target ?? 'AR'];
  const key = `${io.siteId}:${listId}`;
  if (!filterUnsupported.has(key)) {
    try {
      const items = await listItemEnvelopes(io.client, io.siteId, listId, {
        select: AUDIT_SELECT,
        filter: `fields/${AUDIT_FIELDS.parentRef} eq ${odataLiteral(parentRef)}`,
        retry: io.retry,
      });
      return sortAudit(items.map(mapAuditItem).filter((r) => r.parentRef === parentRef));
    } catch (err) {
      if (errorStatus(err) !== 400) throw err;
      filterUnsupported.add(key);
    }
  }
  const index = await readAuditIndex(io, listId, opts.fresh === true);
  return sortAudit(index.get(parentRef) ?? []);
}

// ---------------------------------------------------------------------------
// Completer + rejections (ISS-188)
// ---------------------------------------------------------------------------

const BY_UPN = /\bby\s+([^\s:;,()<>[\]"']+@[^\s:;,()<>[\]"']+)/i;

/** The UPN in a verb Note (`"<verb> by <upn>…"`, 13-04), lower-cased; undefined if none. */
export function upnFromNote(note: string | null | undefined): string | undefined {
  if (typeof note !== 'string') return undefined;
  const m = BY_UPN.exec(note);
  if (!m) return undefined;
  const upn = m[1].replace(/\.+$/, '').trim().toLowerCase();
  return upn.includes('@') ? upn : undefined;
}

/**
 * Who set `Status = Done` (ISS-188): the LATEST audit row with `FieldChanged = Status` and
 * `NewValue = Done`; the UPN in its Note, else its Author (`createdBy.user.email`). No such row →
 * undefined, and `canDoVerb` then refuses verify / reject / reopen (`completer-unknown`,
 * D-13-05-1). Lower-cased (comparisons ignore case anyway, D-13-05-5).
 */
export function completedByUpn(rows: readonly AuditRecord[]): string | undefined {
  const done = rows.filter(
    (r) => r.fieldChanged === AR_FIELDS.status && (r.newValue ?? '').trim() === 'Done',
  );
  if (done.length === 0) return undefined;
  const latest = sortAudit(done)[done.length - 1];
  const fromNote = upnFromNote(latest.note);
  if (fromNote) return fromNote;
  const author = (latest.authorEmail ?? '').trim().toLowerCase();
  return author === '' ? undefined : author;
}

/** The `Verdict` audit rows: `countRejections`' input (D-13-00-3). */
export function verdictRows(rows: readonly AuditRecord[]): AuditRecord[] {
  return rows.filter((r) => r.fieldChanged === AR_FIELDS.verdict);
}

export interface VerifyContext {
  /** The row's audit trail, oldest first. */
  audit: AuditRecord[];
  /** Pass as `ctx.completedByUpn` to `canDoVerb` (verify / reject / reopen). */
  completedByUpn?: string;
  /** The Verdict rows (`countRejections`' input). */
  verdictRows: AuditRecord[];
  /** Pass as `input.priorRejections` to `reject`. */
  priorRejections: number;
}

/** One audit read → everything verify / reject / reopen need for an Action Register row. */
export async function loadVerifyContext(io: ActionIO, row: Pick<ActionRow, 'id'>): Promise<VerifyContext> {
  const audit = await listAuditFor(io, actionRef(row.id));
  const verdicts = verdictRows(audit);
  return {
    audit,
    completedByUpn: completedByUpn(audit),
    verdictRows: verdicts,
    priorRejections: countRejections(verdicts),
  };
}

// ---------------------------------------------------------------------------
// The parent Incident, read only (13-10 regulatory panel)
// ---------------------------------------------------------------------------

/** The parent Incident's key fields, as the regulatory panel shows them. Read only. */
export interface IncidentContext {
  /** `INC-<id>`. */
  reference: string;
  title: string | null;
  status: string | null;
  category: string | null;
  severity: string | null;
  /** CARD-QS-001's assessment, with its cited sections (a Note). */
  reportabilityAssessment: string | null;
  /** `Pending | NotReportable | SIRS-P1 | SIRS-P2 | NDIS-Reportable | WorkSafe | Other`. */
  reportabilityDecision: string | null;
  /** 0–100. */
  confidence: number | null;
}

/** The card fields on the Incidents list (`extend_incidents_for_card.py`, live 27 Sep 2026). */
export const INCIDENT_CONTEXT_SELECT: readonly string[] = [
  REGISTERS.incidents.fields.title,
  REGISTERS.incidents.fields.status,
  REGISTERS.incidents.extraFields.category,
  REGISTERS.incidents.extraFields.severity,
  'ReportabilityAssessment',
  'ReportabilityDecision',
  'Confidence',
];

/** The Incidents item id in a `ParentId` such as `INC-593`, or null. */
export function incidentIdOf(parentId: string | null | undefined): number | null {
  const m = /^\s*INC-0*(\d+)\s*$/i.exec(parentId ?? '');
  return m ? Number(m[1]) : null;
}

/**
 * One GET of the parent Incident (D-13-07-1 retry policy). Never writes: the panel only shows the
 * record, and a disagreement is said, not fixed (cross-register writes are out of scope).
 */
export async function getIncidentContext(io: ActionIO, parentId: string): Promise<IncidentContext> {
  const id = incidentIdOf(parentId);
  if (id === null) throw new Error(`Not an incident reference: ${parentId}`);
  const listId = await getListId(io.client, io.siteId, REGISTERS.incidents.listTitle);
  const item = await getItemEnvelope(io.client, io.siteId, listId, id, INCIDENT_CONTEXT_SELECT, io.retry);
  const f: Fields = item.fields ?? {};
  const confidence = Number(str(f, 'Confidence'));
  return {
    reference: `INC-${id}`,
    title: str(f, REGISTERS.incidents.fields.title),
    status: str(f, REGISTERS.incidents.fields.status),
    category: str(f, REGISTERS.incidents.extraFields.category),
    severity: str(f, REGISTERS.incidents.extraFields.severity),
    reportabilityAssessment: str(f, 'ReportabilityAssessment'),
    reportabilityDecision: str(f, 'ReportabilityDecision'),
    confidence: str(f, 'Confidence') !== null && Number.isFinite(confidence) ? confidence : null,
  };
}

// ---------------------------------------------------------------------------
// Actor lookup (D-13-00-4)
// ---------------------------------------------------------------------------

const ACTOR_KEY_PREFIX = 'qms.actorLookupId.v1:';

function sessionStoreOf(io: ActionIO): SessionStore | null {
  if (io.sessionStore !== undefined) return io.sessionStore;
  try {
    return (globalThis as { sessionStorage?: SessionStore }).sessionStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * The signed-in user's site user id (their User Information List item id), or undefined. NEVER
 * throws: a user who has never touched the site is not in the list, and the audit row's Author
 * plus the UPN in Note still identify them (D-13-00-4).
 *
 * One query, lower-cased: the live tenant matches `EMail` case-insensitively and answers an
 * unknown user with 200 + an empty `value`. Found ids are cached in sessionStorage (try/catch);
 * "not found" is remembered in memory for this page load only.
 */
export async function resolveActor(io: ActionIO, upn: string | null | undefined): Promise<string | undefined> {
  const u = (upn ?? '').trim().toLowerCase();
  if (!u) return undefined;
  const memoKey = `${io.siteId}|${u}`;
  const memo = actorMemo.get(memoKey);
  if (memo !== undefined) return memo || undefined;

  const store = sessionStoreOf(io);
  const storeKey = ACTOR_KEY_PREFIX + memoKey;
  try {
    const hit = store?.getItem(storeKey);
    if (hit) {
      actorMemo.set(memoKey, hit);
      return hit;
    }
  } catch {
    // storage unavailable: fall through to the query
  }

  const query = (listId: string) =>
    listItemEnvelopes(io.client, io.siteId, listId, {
      select: ['EMail', 'Title'],
      filter: `fields/EMail eq ${odataLiteral(u)}`,
      retry: io.retry,
    });

  try {
    let items: GraphListItemEnvelope[];
    try {
      items = await query(USER_INFO_LIST.id);
    } catch (err) {
      if (errorStatus(err) !== 404) throw err;
      items = await query(await getListId(io.client, io.siteId, USER_INFO_LIST.displayName));
    }
    // Only a row whose EMail really is this UPN: a wrong id would misattribute every audit row.
    const exact = items.find((i) => String(i.fields?.EMail ?? '').trim().toLowerCase() === u);
    const id = exact?.id ? String(exact.id) : undefined;
    actorMemo.set(memoKey, id ?? '');
    if (id) {
      try {
        store?.setItem(storeKey, id);
      } catch {
        // cache write failed: harmless
      }
    }
    return id;
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// Commit: PATCH (If-Match) then audit rows
// ---------------------------------------------------------------------------

export type CommitStage = 'patch' | 'audit';

/**
 * A failed commit. `statusCode` is the HTTP status (the outbox reads it: 412 at the patch stage →
 * `conflict`). `retryAfterMs` is the server's Retry-After on a 429 / 503. `remainingAudit` holds
 * the drafts not yet posted when the audit stage failed.
 */
export class CommitError extends Error {
  readonly stage: CommitStage;
  readonly statusCode?: number;
  readonly retryAfterMs?: number;
  readonly remainingAudit: AuditRowDraft[];

  constructor(stage: CommitStage, cause: unknown, remainingAudit: AuditRowDraft[] = [], statusOverride?: number) {
    const status = statusOverride ?? errorStatus(cause) ?? undefined;
    const retryAfterMs = status === 429 || status === 503 ? retryAfterOf(cause) : undefined;
    const base = cause instanceof Error ? cause.message : String(cause);
    super(`${stage} failed${status ? ` (${status})` : ''}: ${base}`);
    this.name = 'CommitError';
    this.stage = stage;
    if (status !== undefined) this.statusCode = status;
    if (retryAfterMs !== undefined) this.retryAfterMs = retryAfterMs;
    this.remainingAudit = remainingAudit;
  }
}

/** null / undefined / '' are one "empty"; ISO instants compare by time; CRLF ≡ LF. */
function sameValue(current: unknown, wanted: unknown): boolean {
  const empty = (v: unknown) => v === null || v === undefined || v === '';
  if (empty(current) || empty(wanted)) return empty(current) && empty(wanted);
  const a = String(current).replace(/\r\n/g, '\n');
  const b = String(wanted).replace(/\r\n/g, '\n');
  if (a === b) return true;
  const iso = /^\d{4}-\d{2}-\d{2}T/;
  if (iso.test(a) && iso.test(b)) {
    const ta = Date.parse(a);
    const tb = Date.parse(b);
    return !Number.isNaN(ta) && ta === tb;
  }
  return false;
}

/**
 * After a 412: does the row already hold every value of our patch, and (when Graph says) were we
 * its last editor? Then the PATCH landed before a crash and the change is ours (13-06 limit (a)).
 */
export function patchAlreadyApplied(
  current: GraphListItemEnvelope,
  patch: Record<string, unknown>,
  ownerUpn?: string,
): boolean {
  const f: Fields = current.fields ?? {};
  const keys = Object.keys(patch);
  if (keys.length === 0) return false;
  if (!keys.every((k) => sameValue(f[k], patch[k]))) return false;
  const editor = current.lastModifiedBy?.user?.email;
  if (ownerUpn && typeof editor === 'string' && editor.trim() !== '') {
    return editor.trim().toLowerCase() === ownerUpn.trim().toLowerCase();
  }
  return true;
}

interface CommitSpec {
  target: ListKind;
  itemId: number;
  eTag: string;
  patch: Record<string, unknown>;
  audit: AuditRowDraft[];
  patched: boolean;
  auditPosted: number[];
  /** The outbox action id: enables the audit de-dupe. */
  actionId?: string;
  /** Whose change this is (the 412 "was it ours" check). */
  ownerUpn?: string;
  /** A retry or replay: de-dupe each audit row against the list before posting. */
  retrying: boolean;
}

/** Audit POSTs are not idempotent: only a 429 (refused before processing) is retried in-call. */
function auditRetry(io: ActionIO): RetryOptions {
  return { ...io.retry, retryOn: [429] };
}

async function commitCore(io: ActionIO, spec: CommitSpec, progress: CommitProgress): Promise<CommitResult> {
  const parentList = PARENT_LIST[spec.target];
  const auditList = AUDIT_LIST[spec.target];
  let eTag: string | undefined;
  let adopted = false;

  if (!spec.patched) {
    if (Object.keys(spec.patch).length > 0) {
      try {
        ({ eTag } = await updateItemFields(io.client, io.siteId, parentList, spec.itemId, spec.patch, spec.eTag, io.retry));
      } catch (err) {
        if (errorStatus(err) !== 412) throw new CommitError('patch', err, spec.audit);
        let current: GraphListItemEnvelope;
        try {
          current = await getItemEnvelope(io.client, io.siteId, parentList, spec.itemId, Object.keys(spec.patch), io.retry);
        } catch (readErr) {
          // A deleted row is a conflict; any other read failure is retried later (unknown ≠ conflict).
          if (errorStatus(readErr) === 404) throw new CommitError('patch', err, spec.audit, 412);
          throw new CommitError('patch', readErr, spec.audit);
        }
        if (!patchAlreadyApplied(current, spec.patch, spec.ownerUpn)) {
          throw new CommitError('patch', err, spec.audit, 412);
        }
        eTag = eTagOf(current);
        adopted = true;
      }
    }
    progress.patched();
  }

  const toPost = spec.audit
    .map((draft, index) => ({ draft, index }))
    .filter(({ index }) => !spec.auditPosted.includes(index));
  const remaining = (from: number) => toPost.slice(from).map((x) => x.draft);

  let existing: AuditRecord[] | null = null;
  if (toPost.length > 0 && spec.actionId && (spec.retrying || adopted)) {
    try {
      existing = await listAuditFor(io, toPost[0].draft.parentRef, { target: spec.target, fresh: true });
    } catch (err) {
      // Never post blind on a retry: the row might already be there.
      throw new CommitError('audit', err, remaining(0));
    }
  }

  for (let i = 0; i < toPost.length; i += 1) {
    const { draft, index } = toPost[i];
    const already =
      existing !== null &&
      existing.some(
        (r) => noteHasAction(r.note, spec.actionId as string) && (r.fieldChanged ?? '') === (draft.fieldChanged ?? ''),
      );
    if (!already) {
      try {
        await createItemFields(io.client, io.siteId, auditList, auditFields(draft), auditRetry(io));
      } catch (err) {
        throw new CommitError('audit', err, remaining(i));
      }
    }
    progress.auditPosted(index);
  }
  return eTag ? { eTag } : {};
}

/**
 * The outbox's `commit` (13-06 contract): unless `entry.patched`, PATCH with If-Match and call
 * `progress.patched()`; then post each unposted audit draft and call `progress.auditPosted(i)`.
 * Returns `{ eTag }` from the PATCH. Throws `CommitError` (with `stage`, `statusCode`,
 * `retryAfterMs`).
 */
export function commitVerb(io: ActionIO, entry: OutboxEntry, progress: CommitProgress): Promise<CommitResult> {
  return commitCore(
    io,
    {
      target: 'AR',
      itemId: entry.itemId,
      eTag: entry.eTag,
      patch: entry.patch,
      audit: entry.audit,
      patched: entry.patched,
      auditPosted: entry.auditPosted,
      actionId: entry.actionId,
      ownerUpn: entry.ownerUpn,
      retrying: entry.attempts > 0 || entry.patched || entry.auditPosted.length > 0 || entry.late,
    },
    progress,
  );
}

/**
 * The outbox's `findCommitted`: Graph cannot `$filter` a multi-line Note, so filter by ParentRef
 * and scan for `[a:<actionId>]`. Always a fresh read.
 */
export async function findCommitted(io: ActionIO, actionId: string, entry: OutboxEntry): Promise<boolean> {
  const parentRef = entry.audit[0]?.parentRef ?? actionRef(entry.itemId);
  const rows = await listAuditFor(io, parentRef, { fresh: true });
  return rows.some((r) => noteHasAction(r.note, actionId));
}

/**
 * What 13-08 hands `createOutbox`. `retryAfterMsFor(actionId)` is the last Retry-After a commit
 * of that action met (the outbox itself stores only status and message).
 */
export function outboxBindings(io: ActionIO): {
  commit: CommitFn;
  findCommitted: (actionId: string, entry: OutboxEntry) => Promise<boolean>;
  retryAfterMsFor: (actionId: string) => number | undefined;
} {
  const lastRetryAfter = new Map<string, number>();
  return {
    commit: async (entry, progress) => {
      try {
        const res = await commitVerb(io, entry, progress);
        lastRetryAfter.delete(entry.actionId);
        return res;
      } catch (err) {
        if (err instanceof CommitError && err.retryAfterMs !== undefined) {
          lastRetryAfter.set(entry.actionId, err.retryAfterMs);
        }
        throw err;
      }
    },
    findCommitted: (actionId, entry) => findCommitted(io, actionId, entry),
    retryAfterMsFor: (actionId) => lastRetryAfter.get(actionId),
  };
}

// ---------------------------------------------------------------------------
// Incident follow-ups (13.2-07; D-13.2-A-4, D-13.2-P-7): create the AR row, then its "created" audit row
// ---------------------------------------------------------------------------

/** `buildFollowUp`'s success output (13.2-06): the AR fields and the unstamped "created" audit row. */
export interface FollowUpCreate {
  fields: Readonly<Record<string, string>>;
  audit: CreatedAuditDraft;
}

/** What a follow-up create returns. Practice mode creates nothing, so its `id` / `ref` are null. */
export interface FollowUpCreated {
  id: number | null;
  /** `ACT-<id>`. */
  ref: string | null;
  eTag?: string;
}

/**
 * The only columns a follow-up create may carry (what `buildFollowUp` writes). An unknown field fails the whole Graph
 * POST (the ISS-285 class), so anything else is refused before the request is sent.
 */
export const FOLLOW_UP_FIELD_KEYS: ReadonlySet<string> = new Set<string>([
  AR_FIELDS.title,
  AR_FIELDS.kind,
  AR_FIELDS.source,
  AR_FIELDS.parentType,
  AR_FIELDS.sourceRef,
  AR_FIELDS.parentId,
  AR_FIELDS.actionType,
  AR_FIELDS.description,
  AR_FIELDS.ownerRole,
  AR_LOOKUP_FIELDS.owner,
  AR_FIELDS.dueDate,
  AR_FIELDS.executor,
  AR_FIELDS.status,
  AR_FIELDS.acceptedAt,
  AR_FIELDS.releasedAt,
  AR_FIELDS.releasedBy,
  AR_FIELDS.tier,
  AR_FIELDS.verificationMethod,
]);

/** `INC-<n>`, or a practice record's `INC-P<n>` (the practice writer only; live refuses it). */
const FOLLOW_UP_LINK = /^INC-(P?)\d+$/;

/**
 * Throws unless `fields` is a follow-up create in the 13.2-06 convention: only `FOLLOW_UP_FIELD_KEYS`, an Action
 * linked `Source = ParentType = Incident` with `SourceRef = ParentId = INC-<n>`, an owner, a bare due day, and
 * released by a person. Both writers run it, so practice refuses what live would refuse.
 */
export function assertFollowUpFields(fields: Readonly<Record<string, unknown>>): void {
  const bad = Object.keys(fields).filter((k) => !FOLLOW_UP_FIELD_KEYS.has(k));
  if (bad.length > 0) throw new Error(`createFollowUp: refusing column(s) a follow-up never writes: ${bad.join(', ')}`);
  const F = AR_FIELDS;
  const v = (k: string) => (typeof fields[k] === 'string' ? (fields[k] as string) : '');
  const problems: string[] = [];
  if (v(F.kind) !== 'Action') problems.push('Kind is not Action');
  if (v(F.source) !== 'Incident' || v(F.parentType) !== 'Incident') problems.push('Source and ParentType must be Incident');
  if (!FOLLOW_UP_LINK.test(v(F.parentId)) || v(F.sourceRef) !== v(F.parentId)) problems.push('SourceRef and ParentId must both be INC-<id>');
  if (v(F.title).trim() === '') problems.push('no Title');
  if (v(AR_LOOKUP_FIELDS.owner).trim() === '') problems.push('no Owner');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v(F.dueDate))) problems.push('DueDate is not a bare YYYY-MM-DD day');
  if (v(F.releasedAt) === '' || v(F.releasedBy).trim() === '') problems.push('not released by a person');
  if (problems.length > 0) throw new Error(`createFollowUp: not a follow-up create: ${problems.join('; ')}`);
}

/** A practice record's follow-up (`INC-P<n>`): the practice writer may log it; live never sends it. */
export function isPracticeFollowUp(fields: Readonly<Record<string, unknown>>): boolean {
  return FOLLOW_UP_LINK.exec(String(fields[AR_FIELDS.parentId] ?? ''))?.[1] === 'P';
}

/**
 * The follow-up's audit row, completed with the new item id (`ACT-<id> created`) and tagged `[a:<commitId>]`, so a
 * re-post can de-dupe on it (D-13-07-3). `id = null` (practice) stamps the readable placeholder `ACT-<new id>`.
 */
export function followUpAuditRow(draft: CreatedAuditDraft, id: number | null, commitId: string): AuditRowDraft {
  const stamped = id === null ? { ...draft, title: 'ACT-<new id> created', parentRef: 'ACT-<new id>' } : stampCreatedAudit(draft, id);
  const tag = actionTag(commitId);
  return { ...stamped, note: stamped.note ? `${stamped.note} ${tag}` : tag };
}

/**
 * Create one incident follow-up (13.2-07): POST the AR item (`createItemFields`), then POST its "created" audit row
 * to the Action Register Audit Log with the new id filled in. The audit row never carries `ActorUPN` (that list has
 * no such column, ISS-285): the UPN is in its `Note`, and `ActorLookupId` is sent when it resolved.
 *
 * Retry policy: both POSTs are retried only on a 429 (refused before processing); a create is not idempotent.
 *   - The item POST fails → `CommitError('patch')`: nothing was created (or, on a reply with no id, the message says to
 *     check the register before adding it again).
 *   - The audit POST fails after the item landed → `CommitError('audit', …, [the tagged row])`. The follow-up exists;
 *     its audit row is owed. The caller offers "Post the audit row" (`postFollowUpAudit`, de-duped). The item is never
 *     POSTed again.
 */
export async function createFollowUp(io: ActionIO, built: FollowUpCreate): Promise<FollowUpCreated> {
  assertFollowUpFields(built.fields);
  if (isPracticeFollowUp(built.fields)) throw new Error('createFollowUp: a practice record\'s follow-up is never written');
  let created: { id: string; eTag?: string };
  try {
    created = await createItemFields(io.client, io.siteId, AR_LISTS.actionRegister, { ...built.fields }, auditRetry(io));
  } catch (err) {
    throw new CommitError('patch', err);
  }
  const id = Number(created.id);
  if (!Number.isSafeInteger(id) || id < 1) {
    throw new CommitError('patch', new Error('the create returned no item id: check the Action Register before adding it again'));
  }
  const row = followUpAuditRow(built.audit, id, (io.randomId ?? (() => crypto.randomUUID()))());
  try {
    await createItemFields(io.client, io.siteId, AR_LISTS.actionRegisterAudit, auditFields(row), auditRetry(io));
  } catch (err) {
    throw new CommitError('audit', err, [row]);
  }
  return { id, ref: actionRef(id), ...(created.eTag ? { eTag: created.eTag } : {}) };
}

/**
 * Post a follow-up's owed audit row(s) again (a `createFollowUp` `CommitError('audit')`'s `remainingAudit`): per
 * `ACT-<id>`, ONE fresh read of its audit rows, and a draft is skipped when a row already carries its `[a:<id>]` tag
 * and the same `FieldChanged` (a POST that did land). If that read fails, nothing is posted for it. An untagged draft
 * is never posted blind. Returns what is still owed (empty = the trail is complete). 429-only retry.
 */
export async function postFollowUpAudit(io: ActionIO, drafts: readonly AuditRowDraft[]): Promise<AuditRowDraft[]> {
  const byParent = new Map<string, AuditRowDraft[]>();
  for (const d of drafts) byParent.set(d.parentRef, [...(byParent.get(d.parentRef) ?? []), d]);
  const still: AuditRowDraft[] = [];
  for (const [parentRef, group] of byParent) {
    const untagged = group.filter((d) => !auditTagOf(d.note));
    const tagged = group.filter((d) => auditTagOf(d.note));
    still.push(...untagged);
    if (tagged.length === 0) continue;
    let existing: AuditRecord[];
    try {
      existing = await listAuditFor(io, parentRef, { target: 'AR', fresh: true });
    } catch {
      still.push(...tagged);
      continue;
    }
    for (let i = 0; i < tagged.length; i += 1) {
      const draft = tagged[i];
      const tagId = auditTagOf(draft.note) as string;
      if (existing.some((r) => noteHasAction(r.note, tagId) && (r.fieldChanged ?? '') === (draft.fieldChanged ?? ''))) continue;
      try {
        await createItemFields(io.client, io.siteId, AR_LISTS.actionRegisterAudit, auditFields(draft), auditRetry(io));
      } catch {
        still.push(...tagged.slice(i));
        break;
      }
    }
  }
  return still;
}

/** Post audit drafts directly (e.g. a `CommitError.remainingAudit` retry). 429-only retry. */
export async function postAuditRows(io: ActionIO, target: ListKind, drafts: readonly AuditRowDraft[]): Promise<void> {
  for (let i = 0; i < drafts.length; i += 1) {
    try {
      await createItemFields(io.client, io.siteId, AUDIT_LIST[target], auditFields(drafts[i]), auditRetry(io));
    } catch (err) {
      throw new CommitError('audit', err, drafts.slice(i));
    }
  }
}

// ---------------------------------------------------------------------------
// Verification Log verify (13-11)
// ---------------------------------------------------------------------------

/** The verdicts the Verify page writes (`pending` is the starting state, `packet-delivered` a card's). */
export const VL_VERIFY_VERDICTS = ['verified', 'rejected', 'sampled-ok'] as const;
export type VlVerifyVerdict = (typeof VL_VERIFY_VERDICTS)[number];

/** The patch keys a VL patch may carry: `VL_MUTABLE_FIELDS`, with `VerifiedBy` as its LookupId. */
const VL_PATCH_KEYS = new Set<string>([...VL_MUTABLE_FIELDS, VL_LOOKUP_FIELDS.verifiedBy]);

/** Throws if a Verification Log patch touches anything outside `VL_MUTABLE_FIELDS` (ISS-116). */
export function assertVlMutable(patch: Record<string, unknown>): void {
  const bad = Object.keys(patch).filter((k) => !VL_PATCH_KEYS.has(k));
  if (bad.length > 0) {
    throw new Error(`verifyVL: refusing to change non-mutable Verification Log field(s): ${bad.join(', ')}`);
  }
}

/**
 * The VL verify as data: `{ patch, audit }` over `VL_MUTABLE_FIELDS` only, one `StateChange` draft
 * (`ParentRef = VER-<id>`) per changed field. Built by `lib/vlVerbs` (13-11), so the live and the
 * practice-mode writers send exactly what that pure, tested module builds: pending rows only,
 * Oversight or a holder of the row's `VerifierRole` (D-13-R-6), never the row's own
 * `AgentIdentity` (E4), `VerifierRole` stamped only when blank, and a rejection's reason
 * (≥ 10 characters) in `ExceptionReason`.
 */
export function buildVlVerify(
  row: VerificationRow,
  verdict: VlVerifyVerdict,
  actor: Actor,
  opts: { reason?: string; now?: Date | string | number } = {},
): VerbResult {
  if (!(VL_VERIFY_VERDICTS as readonly string[]).includes(verdict)) {
    throw new VerbError('invalid-verdict', `verifyVL: unknown verdict ${String(verdict)}`);
  }
  const now = new Date(opts.now ?? Date.now());
  const result =
    verdict === 'rejected' ? vlReject(row, actor, now, opts.reason ?? '') : vlVerify(row, actor, now, verdict);
  assertVlMutable(result.patch);
  return result;
}

/**
 * 13-13 (ISS-194): tag every audit draft of one Verification Log commit `[a:<commitId>]`, as the
 * outbox tags Action Register drafts, so a later re-post can de-dupe on it (D-13-07-3). The live
 * and the practice writers both tag, so practice logs exactly what live sends.
 */
export function tagVlAudit(result: VerbResult, commitId: string): VerbResult {
  const tag = actionTag(commitId);
  return { patch: result.patch, audit: result.audit.map((d) => ({ ...d, note: d.note ? `${d.note} ${tag}` : tag })) };
}

/**
 * PATCH a Verification Log row (If-Match) and write its VL audit rows. The patch is asserted to
 * touch `VL_MUTABLE_FIELDS` only, before anything is sent. Each audit Note is tagged
 * `[a:<commitId>]` (ISS-194), so a `CommitError('audit')`'s `remainingAudit` can be re-posted
 * through `postVlAudit` without ever writing a row twice.
 */
export async function commitVlPatch(
  io: ActionIO,
  row: Pick<VerificationRow, 'id' | 'eTag'>,
  result: VerbResult,
  actor: Pick<Actor, 'upn'>,
): Promise<CommitResult> {
  assertVlMutable(result.patch);
  const commitId = (io.randomId ?? (() => crypto.randomUUID()))();
  const tagged = tagVlAudit(result, commitId);
  return commitCore(
    io,
    {
      target: 'VL',
      itemId: row.id,
      eTag: row.eTag,
      patch: tagged.patch,
      audit: tagged.audit,
      patched: false,
      auditPosted: [],
      actionId: commitId,
      ownerUpn: actor.upn,
      retrying: false,
    },
    { patched: () => undefined, auditPosted: () => undefined },
  );
}

/** VL audit drafts still to post, per Verification Log row (a `CommitError.remainingAudit`, or bulk). */
export type VlAuditPending = Array<{ id: number; drafts: AuditRowDraft[] }>;

const TAG_ID = /\[a:([^\]\s]+)\]/;

/** The `<id>` of the `[a:<id>]` tag in an audit Note, if any. */
export function auditTagOf(note: string | null | undefined): string | undefined {
  const m = typeof note === 'string' ? TAG_ID.exec(note) : null;
  return m ? m[1] : undefined;
}

/**
 * 13-13 (ISS-194, D-13-07-3): post Verification Log audit drafts that failed after their PATCH
 * landed. Per row, ONE fresh read of `VER-<id>`'s audit rows; a draft is skipped when a row already
 * carries its `[a:<id>]` tag with the same `FieldChanged` (a 503 that did land). If that read fails,
 * nothing is posted for the row. A draft with no tag cannot be de-duped, so it is never posted
 * blind. Returns the drafts still not posted (empty = the trail is complete).
 */
export async function postVlAudit(io: ActionIO, pending: VlAuditPending): Promise<VlAuditPending> {
  const byRow = new Map<number, AuditRowDraft[]>();
  for (const p of pending) byRow.set(p.id, [...(byRow.get(p.id) ?? []), ...p.drafts]);
  const still: VlAuditPending = [];
  const keep = (id: number, drafts: AuditRowDraft[]) => {
    if (drafts.length) still.push({ id, drafts });
  };
  for (const [id, drafts] of byRow) {
    const untagged = drafts.filter((d) => !auditTagOf(d.note));
    const tagged = drafts.filter((d) => auditTagOf(d.note));
    let existing: AuditRecord[];
    try {
      existing = tagged.length ? await listAuditFor(io, tagged[0].parentRef || `VER-${id}`, { target: 'VL', fresh: true }) : [];
    } catch {
      keep(id, drafts);
      continue;
    }
    const left: AuditRowDraft[] = [...untagged];
    for (let i = 0; i < tagged.length; i += 1) {
      const draft = tagged[i];
      const tagId = auditTagOf(draft.note) as string;
      const landed = existing.some(
        (r) => noteHasAction(r.note, tagId) && (r.fieldChanged ?? '') === (draft.fieldChanged ?? ''),
      );
      if (landed) continue;
      try {
        await createItemFields(io.client, io.siteId, AUDIT_LIST.VL, auditFields(draft), auditRetry(io));
      } catch {
        left.push(...tagged.slice(i));
        break;
      }
    }
    keep(id, left);
  }
  return still;
}

/** Verify / reject a Verification Log row: `buildVlVerify` then `commitVlPatch`. */
export async function verifyVL(
  io: ActionIO,
  row: VerificationRow,
  verdict: VlVerifyVerdict,
  actor: Actor,
  opts: { reason?: string; now?: Date | string | number } = {},
): Promise<CommitResult> {
  const result = buildVlVerify(row, verdict, actor, { ...opts, now: opts.now ?? io.now?.() });
  return commitVlPatch(io, row, result, actor);
}

/** Options for `correctVL`: the acting time and, when this session gave the verdict, its UPN. */
export type VlCorrectOpts = VlCorrectContext & { now?: Date | string | number };

/**
 * Correct a mistaken Verification Log verdict the same Melbourne day (ISS-193 / D-13-R-5):
 * `lib/vlVerbs.vlCorrect` builds it (the giver or Oversight, a reason, five fields at most, one
 * audit row per changed field), then `commitVlPatch` sends it with If-Match.
 */
export async function correctVL(
  io: ActionIO,
  row: VerificationRow,
  newVerdict: VlCorrectVerdict,
  reason: string,
  actor: Actor,
  opts: VlCorrectOpts = {},
): Promise<CommitResult> {
  const { now, ...ctx } = opts;
  const result = vlCorrect(row, newVerdict, reason, actor, new Date(now ?? io.now?.() ?? Date.now()), ctx);
  return commitVlPatch(io, row, result, actor);
}

// ---------------------------------------------------------------------------
// The release sitting: bulk release / retire via sequential $batch (13-12)
// ---------------------------------------------------------------------------

export interface BulkSummary {
  verb: 'release' | 'retire';
  /** The tag (`[a:<sittingId>]`) on every audit Note of this sitting. */
  sittingId: string;
  /** Item ids whose PATCH returned 2xx (the plan's `released`, for either verb). */
  applied: number[];
  /** 412: changed by someone else since it was read. Refetch. */
  conflicts: number[];
  /**
   * Rows re-sent after a 429 / 503 that did NOT end in `failed` (each row counted once). 13-13
   * (ISS-196): a row still throttled at the end is "to retry" only, never also "resent".
   */
  retried: number;
  /** PATCH failures (no reply, other status, or still throttled after `maxRounds`). */
  failed: Array<{ id: number; status: number | null; reason: string }>;
  /** Refused before sending: the verb builder or `canDoVerb` said no. */
  refused: Array<{ id: number; code: string }>;
  /** Audit rows that did NOT post for an applied PATCH. Retry with `postAuditRows`. */
  auditPending: Array<{ id: number; draft: AuditRowDraft }>;
  /** The new eTag of each applied row, when the reply carried one. */
  eTags: Record<number, string>;
  /**
   * 13-12: the longest `Retry-After` (ms) the last throttled round asked for, when rows ended
   * `failed` as `throttled`. A retry of those rows waits at least this long.
   */
  retryAfterMs?: number;
}

/** 13-12: per-chunk progress of a sitting, for the "Sending batch 3 of 10" line. */
export interface BulkProgress {
  /** The chunk just finished (1-based). */
  chunk: number;
  chunks: number;
  /** Rows whose chunk has finished, whatever the outcome. */
  done: number;
  /** Rows to be sent (refused rows are not counted). */
  total: number;
}

export interface BulkRunOptions {
  now?: Date | string | number;
  maxRounds?: number;
  /** Called after each chunk of 20 finishes. */
  onProgress?: (p: BulkProgress) => void;
}

interface BulkWork {
  row: ActionRow;
  result: VerbResult;
}

/**
 * 13-13 (ISS-196, D-13-07-1): the longest Retry-After a sitting waits out in-call. The same cap as
 * `withRetry`'s `maxWaitMs` (default 30 s); anything longer goes back to the page as throttled, with
 * `retryAfterMs`, so the progress line never freezes and the page's countdown covers the wait.
 */
export const BULK_MAX_WAIT_MS = 30_000;

function overCap(io: ActionIO, retryAfterMs: number | undefined): boolean {
  return retryAfterMs !== undefined && retryAfterMs > (io.retry?.maxWaitMs ?? BULK_MAX_WAIT_MS);
}

async function postBulkAudits(
  io: ActionIO,
  ok: readonly BatchResult[],
  work: Map<number, BulkWork>,
  summary: BulkSummary,
  maxRounds: number,
  sleep: (ms: number) => Promise<void>,
): Promise<void> {
  const target: ListTarget = { siteId: io.siteId, listId: AR_LISTS.actionRegisterAudit };
  const requests = buildAuditRequests(ok, (r) => (work.get(Number(r.request.id))?.result.audit ?? []).map(auditFields), target);
  const draftOf = new Map<string, { id: number; draft: AuditRowDraft }>();
  for (const r of ok) {
    const w = work.get(Number(r.request.id));
    w?.result.audit.forEach((draft, n) => draftOf.set(`${r.request.id}-a${n}`, { id: w.row.id, draft }));
  }
  const pend = (reqs: readonly GraphBatchRequest[]) => {
    for (const q of reqs) {
      const d = draftOf.get(q.id);
      if (d) summary.auditPending.push(d);
    }
  };

  for (const group of chunk(requests, MAX_BATCH)) {
    let pending: GraphBatchRequest[] = group;
    for (let round = 0; pending.length > 0; round += 1) {
      let responses;
      try {
        responses = await postBatch(io.client, pending, auditRetry(io));
      } catch {
        pend(pending);
        break;
      }
      const p = partition(pending, responses, (io.now ?? Date.now)());
      pend(p.conflict412.map((x) => x.request));
      pend(p.failed.filter((f) => f.request && (f.reason === 'status' || f.reason === 'no-response')).map((f) => f.request as GraphBatchRequest));
      if (p.throttled.items.length === 0) break;
      // ISS-196: never sit on a Retry-After over the cap; the page's "Post the N audit rows" takes over.
      if (round + 1 >= maxRounds || overCap(io, p.throttled.retryAfterMs)) {
        pend(p.throttled.items.map((x) => x.request));
        break;
      }
      await sleep(p.throttled.retryAfterMs ?? backoffMs(round, io.retry?.random));
      pending = p.throttled.items.map((x) => x.request);
    }
  }
}

async function runBulk(
  io: ActionIO,
  verb: 'release' | 'retire',
  rows: readonly ActionRow[],
  note: string,
  actor: Actor,
  opts: BulkRunOptions,
): Promise<BulkSummary> {
  const sleep = io.retry?.sleep ?? realSleep;
  const maxRounds = Math.max(1, opts.maxRounds ?? 4);
  const at = new Date(opts.now ?? (io.now ?? Date.now)()).toISOString();
  const sittingId = (io.randomId ?? (() => crypto.randomUUID()))();
  const tag = actionTag(sittingId);
  const summary: BulkSummary = {
    verb,
    sittingId,
    applied: [],
    conflicts: [],
    retried: 0,
    failed: [],
    refused: [],
    auditPending: [],
    eTags: {},
  };

  const work = new Map<number, BulkWork>();
  for (const row of rows) {
    if (work.has(row.id)) continue;
    const allowed = canDoVerb(verb, row, actor);
    if (!allowed.ok) {
      summary.refused.push({ id: row.id, code: allowed.reason });
      continue;
    }
    try {
      const result = buildVerb(verb, row, actor, { note, bulk: true }, at);
      if (Object.keys(result.patch).length === 0) {
        summary.refused.push({ id: row.id, code: 'no-change' });
        continue;
      }
      result.audit = result.audit.map((d) => ({ ...d, note: d.note ? `${d.note} ${tag}` : tag }));
      work.set(row.id, { row, result });
    } catch (err) {
      if (err instanceof VerbError) summary.refused.push({ id: row.id, code: err.code });
      else throw err;
    }
  }

  const target: ListTarget = { siteId: io.siteId, listId: AR_LISTS.actionRegister };
  const groups = chunk([...work.values()], RELEASE_BATCH_SIZE);
  /** Rows re-sent after a slow-down; counted in `retried` only if they did not end "to retry". */
  const resent = new Set<number>();
  let done = 0;
  for (const [gi, group] of groups.entries()) {
    let pending = group;
    for (let round = 0; pending.length > 0; round += 1) {
      const requests = buildPatchRequests(
        pending.map((w) => w.row),
        (r) => work.get(r.id)?.result.patch ?? {},
        target,
      );
      let responses;
      try {
        responses = await postBatch(io.client, requests, io.retry);
      } catch (err) {
        const status = errorStatus(err);
        const reason = err instanceof Error ? err.message : String(err);
        for (const w of pending) summary.failed.push({ id: w.row.id, status, reason });
        break;
      }
      const p = partition(requests, responses, (io.now ?? Date.now)());
      for (const r of p.ok) {
        const id = Number(r.request.id);
        summary.applied.push(id);
        const e = eTagOf(r.response.body);
        if (e) summary.eTags[id] = e;
      }
      await postBulkAudits(io, p.ok, work, summary, maxRounds, sleep);
      summary.conflicts.push(...p.conflict412.map((r) => Number(r.request.id)));
      for (const f of p.failed) {
        if (!f.request || (f.reason !== 'status' && f.reason !== 'no-response')) continue;
        summary.failed.push({ id: Number(f.request.id), status: f.response?.status ?? null, reason: f.reason });
      }
      if (p.throttled.items.length === 0) break;
      // ISS-196: hand back, never sleep, when rounds run out or the Retry-After is over the cap.
      if (round + 1 >= maxRounds || overCap(io, p.throttled.retryAfterMs)) {
        for (const t of p.throttled.items) {
          summary.failed.push({ id: Number(t.request.id), status: t.response.status, reason: 'throttled' });
        }
        if (p.throttled.retryAfterMs !== undefined) {
          summary.retryAfterMs = Math.max(summary.retryAfterMs ?? 0, p.throttled.retryAfterMs);
        }
        break;
      }
      for (const t of p.throttled.items) resent.add(Number(t.request.id));
      await sleep(p.throttled.retryAfterMs ?? backoffMs(round, io.retry?.random));
      pending = p.throttled.items
        .map((t) => work.get(Number(t.request.id)))
        .filter((w): w is BulkWork => w !== undefined);
    }
    done += group.length;
    opts.onProgress?.({ chunk: gi + 1, chunks: groups.length, done, total: work.size });
  }
  // ISS-196 copy: "N resent after a slow-down" counts each row once, and never one that is also "to retry".
  const toRetry = new Set(summary.failed.map((f) => f.id));
  summary.retried = [...resent].filter((id) => !toRetry.has(id)).length;
  return summary;
}

/**
 * 13-12: post a sitting's `auditPending` drafts again, de-duped like the outbox (D-13-07-3): per
 * row, one FRESH read of its audit rows, and a draft is skipped when a row already carries this
 * sitting's `[a:<sittingId>]` tag and the same `FieldChanged`. If that read fails, nothing is
 * posted for the row. Returns the drafts still not posted (empty = the trail is complete).
 */
export async function retryBulkAudit(
  io: ActionIO,
  sittingId: string,
  pending: BulkSummary['auditPending'],
): Promise<BulkSummary['auditPending']> {
  const byRow = new Map<number, AuditRowDraft[]>();
  for (const p of pending) byRow.set(p.id, [...(byRow.get(p.id) ?? []), p.draft]);
  const still: BulkSummary['auditPending'] = [];
  for (const [id, drafts] of byRow) {
    let existing: AuditRecord[];
    try {
      existing = await listAuditFor(io, drafts[0].parentRef || actionRef(id), { target: 'AR', fresh: true });
    } catch {
      drafts.forEach((draft) => still.push({ id, draft }));
      continue;
    }
    for (let i = 0; i < drafts.length; i += 1) {
      const draft = drafts[i];
      const landed = existing.some(
        (r) => noteHasAction(r.note, sittingId) && (r.fieldChanged ?? '') === (draft.fieldChanged ?? ''),
      );
      if (landed) continue;
      try {
        await createItemFields(io.client, io.siteId, AUDIT_LIST.AR, auditFields(draft), auditRetry(io));
      } catch {
        drafts.slice(i).forEach((d) => still.push({ id, draft: d }));
        break;
      }
    }
  }
  return still;
}

/**
 * Release `rows` to their owners with one shared note, in sequential `$batch` chunks of 20:
 * PATCH batch → partition → audit batch for the 2xx → re-batch the throttled after the longest
 * Retry-After. Oversight only (`canDoVerb`); a refused row is reported, never sent.
 */
export function releaseBatch(
  io: ActionIO,
  rows: readonly ActionRow[],
  note: string,
  actor: Actor,
  opts: BulkRunOptions = {},
): Promise<BulkSummary> {
  return runBulk(io, 'release', rows, note, actor, opts);
}

/** Retire `rows` (Status = Retired, the note to ReleaseNote), same sequence as `releaseBatch`. */
export function retireBatch(
  io: ActionIO,
  rows: readonly ActionRow[],
  note: string,
  actor: Actor,
  opts: BulkRunOptions = {},
): Promise<BulkSummary> {
  return runBulk(io, 'retire', rows, note, actor, opts);
}
